import React, { useState, useEffect } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import {
  Inventory2,
  CheckCircle,
  AccessTime,
  AttachMoney,
  LocalOffer,
} from "@mui/icons-material";
import Paper from "@mui/material/Paper";
import DataTable from "../../../Requirement/components/common/ReusableTable";
import { fetchData } from "../../../../services/BackendServices";
import dayjs from "dayjs";
const MyTradeDashboard = () => {
  // State for dynamic data
  const [pieData, setPieData] = useState([]);
  const [dashboardCounts, setDashboardCounts] = useState({
    rfq: 0,
    matchOrders: 0,
    negotiationsOrders: 0,
    finalizedOrders: 0,
  });
  const [allStockData, setAllStockData] = useState([]);
  const [allOfferData, setAllOfferData] = useState([]);
  const [allRequirementData, setAllRequirementData] = useState([]);
  const [allRfqData, setAllRfqData] = useState([]);
  const [tableData, setTableData] = useState([]);

  // Individual loading states for each section
  const [loadingStates, setLoadingStates] = useState({
    cards: true,
    pieChart: true,
    stockChart: true,
    offerChart: true,
    requirementChart: true,
    rfqChart: true,
    recentActivity: true,
  });

  // Dynamic cards based on fetched data
  const cards = [
    {
      title: "Active RFQs",
      value: dashboardCounts.rfq || 0,
      icon: <Inventory2 />,
      iconColor: "#1565c0",
      bgColor: "#e3f2fd",
      textColor: "#0d47a1",
    },
    {
      title: "Matched Offers",
      value: dashboardCounts.matchOrders || 0,
      icon: <CheckCircle />,
      iconColor: "#2e7d32",
      bgColor: "#e8f5e9",
      textColor: "#1b5e20",
    },
    {
      title: "Negotiations",
      value: dashboardCounts.negotiationsOrders || 0,
      icon: <AccessTime />,
      iconColor: "#f9a825",
      bgColor: "#fffde7",
      textColor: "#f57f17",
    },
    {
      title: "Accepted Deals",
      value: dashboardCounts.finalizedOrders || 0,
      icon: <AttachMoney />,
      iconColor: "#6a1b9a",
      bgColor: "#f3e5f5",
      textColor: "#4a148c",
    },
  ];

  // Fetch data from backend
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        console.log("setLoading");
        // Fetch all data in parallel
        const promises = [
          // Fetch count status
          fetchData({
            target: "dashboardStatus",
            dataType: "countStatus",
          })
            .then((response) => {
              if (response.success) {
                setDashboardCounts(response.data);
              }
              return response;
            })
            .catch((error) => {
              console.error("Error fetching count status:", error);
              return null;
            })
            .finally(() => {
              setLoadingStates((prev) => ({ ...prev, cards: false }));
            }),

          // Fetch RFQ by status for pie chart
          fetchData({
            target: "dashboardStatus",
            dataType: "rfqByStatus",
          })
            .then((response) => {
              if (response.success && response.data.rfqByStatus) {
                const formattedPieData = response.data.rfqByStatus.map(
                  (item) => ({
                    name: item.Status,
                    value: item.Count,
                    color: getStatusColor(item.Status),
                  })
                );
                setPieData(formattedPieData);
              }
              return response;
            })
            .catch((error) => {
              console.error("Error fetching RFQ status:", error);
              return null;
            })
            .finally(() => {
              setLoadingStates((prev) => ({ ...prev, pieChart: false }));
            }),

          // Fetch stock report by country
          fetchData({
            target: "dashboardStatus",
            dataType: "stockReportByCountry",
            Product: "",
            Country: "",
          })
            .then((response) => {
              if (response.success && response.data.stockReportByCountry) {
                const formattedStockData =
                  response.data.stockReportByCountry.map((item) => ({
                    name: `${item.Product} - ${item.Country}`,
                    stock: item.Stock,
                    country: item.Country,
                  }));
                setAllStockData(formattedStockData);
              }
              return response;
            })
            .catch((error) => {
              console.error("Error fetching stock data:", error);
              return null;
            })
            .finally(() => {
              setLoadingStates((prev) => ({ ...prev, stockChart: false }));
            }),

          // Fetch offer report by country
          fetchData({
            target: "dashboardStatus",
            dataType: "offerReportByCountry",
          })
            .then((response) => {
              console.log(response.data, "offerReportByCountry.");
              if (response.success && response.data.offerReportByCountry) {
                const filteredBuyerData =
                  response.data.offerReportByCountry.filter(
                    (entry) => entry.Country !== null
                  );
                const formattedOfferData = filteredBuyerData.map((item) => ({
                  country: item.Country,
                  offers: item.Offers,
                }));
                setAllOfferData(formattedOfferData);
              }
              return response;
            })
            .catch((error) => {
              console.error("Error fetching offer data:", error);
              return null;
            })
            .finally(() => {
              setLoadingStates((prev) => ({ ...prev, offerChart: false }));
            }),

          // Fetch requirement report by country
          fetchData({
            target: "dashboardStatus",
            dataType: "requirementReportByCountry",
          })
            .then((response) => {
              console.log(response.data, "requirementReportByCountry.");
              if (
                response.success &&
                response.data.requirementReportByCountry
              ) {
                const formattedRequirementData =
                  response.data.requirementReportByCountry.map((item) => ({
                    country: item.Country,
                    requirements: item.Offers,
                  }));
                setAllRequirementData(formattedRequirementData);
              }
              return response;
            })
            .catch((error) => {
              console.error("Error fetching requirement data:", error);
              return null;
            })
            .finally(() => {
              setLoadingStates((prev) => ({
                ...prev,
                requirementChart: false,
              }));
            }),

          // Fetch RFQ report by country
          fetchData({
            target: "dashboardStatus",
            dataType: "rfqReportByCountry",
          })
            .then((response) => {
              if (response.success && response.data.rfqReportByCountry) {
                const formattedRfqData = response.data.rfqReportByCountry.map(
                  (item) => ({
                    country: item.Country,
                    rfqs: item.Offers,
                  })
                );
                setAllRfqData(formattedRfqData);
              }
              return response;
            })
            .catch((error) => {
              console.error("Error fetching RFQ data:", error);
              return null;
            })
            .finally(() => {
              setLoadingStates((prev) => ({ ...prev, rfqChart: false }));
            }),

          // Fetch recent activity from ModuleLog
          fetchData({
            target: "listModuleLog",
            filterTerms: {},
            userOrder: "CreatedAt DESC",
            rowStart: 0,
            pageSize: 50,
          })
            .then((response) => {
              if (response && response.items) {
                const formattedActivityData = response.items.map((item) => ({
                  module: item.Module || "N/A",
                  event: item.Event || "N/A",
                  info: item.Info || "N/A",
                  subInfo: item.SubInfo || "",
                  date: item.CreatedAt
                    ? dayjs(item.CreatedAt).format("DD MMM YYYY HH:mm")
                    : "N/A",
                  createdBy: item.CreatedBy || "System",
                  id: item.Id || "",
                }));
                setTableData(formattedActivityData);
              }
              return response;
            })
            .catch((error) => {
              console.error("Error fetching recent activity:", error);
              setTableData([]);
              return null;
            })
            .finally(() => {
              setLoadingStates((prev) => ({ ...prev, recentActivity: false }));
            }),
        ];

        // Wait for all promises to complete
        await Promise.allSettled(promises);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };

    fetchDashboardData();
  }, []);

  // Helper function to get status colors
  const getStatusColor = (status) => {
    switch (status) {
      case "Open":
        return "#007bffab";
      case "Matched":
        return "#00c49f";
      case "Negotiation":
        return "#ffbb28";
      case "Shortlisted":
        return "#ff8042";
      case "Closed":
        return "#c300ffac";
      default:
        return "#007bffab";
    }
  };

  // Placeholder loader component
  const PlaceholderLoader = ({ height = "300px", showText = true }) => (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ height, backgroundColor: "#f8f9fa", borderRadius: "8px" }}
    >
      <div className="spinner-border text-primary mb-2" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      {showText && <p className="text-muted mb-0">Loading data...</p>}
    </div>
  );

  // Card placeholder loader
  const CardPlaceholder = () => (
    <div
      className="d-flex align-items-center shadow-sm rounded p-3 h-100"
      style={{ backgroundColor: "#f8f9fa" }}
    >
      <div
        className="d-flex align-items-center justify-content-center rounded-circle me-3"
        style={{
          backgroundColor: "#e9ecef",
          width: "40px",
          height: "40px",
        }}
      >
        <div
          className="spinner-border spinner-border-sm text-secondary"
          role="status"
        >
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
      <div>
        <div className="placeholder-glow">
          <span className="placeholder col-7 mb-1"></span>
          <span className="placeholder col-4"></span>
        </div>
      </div>
    </div>
  );

  const columns = [
    { id: "module", numeric: false, label: "MODULE" },
    {
      id: "event",
      numeric: false,
      label: "EVENT",
      renderCell: (row) => {
        let bgColor = "";
        let textColor = "";

        switch (row.event) {
          case "CREATED":
            bgColor = "#d1fae5";
            textColor = "#065f46";
            break;
          case "UPDATED":
            bgColor = "#e0f2fe";
            textColor = "#0369a1";
            break;
          case "DELETED":
            bgColor = "#fee2e2";
            textColor = "#991b1b";
            break;
          case "APPROVED":
            bgColor = "#d1fae5";
            textColor = "#065f46";
            break;
          case "REJECTED":
            bgColor = "#fef3c7";
            textColor = "#b45309";
            break;
          default:
            bgColor = "#f3f4f6";
            textColor = "#374151";
        }

        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: "4px 10px",
              borderRadius: "999px",
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            {row.event}
          </span>
        );
      },
    },
    { id: "info", numeric: false, label: "DESCRIPTION" },
    { id: "subInfo", numeric: false, label: "DETAILS" },
    { id: "date", numeric: false, label: "DATE & TIME" },
    { id: "createdBy", numeric: false, label: "CREATED BY" },
  ];

  // Dynamic countries list from fetched data
  const countries = React.useMemo(() => {
    const countrySet = new Set(["All"]);

    // Collect countries from all data sources
    allStockData.forEach((item) => countrySet.add(item.country));
    allOfferData.forEach((item) => countrySet.add(item.country));
    console.log(allOfferData);
    allRequirementData.forEach((item) => countrySet.add(item.country));
    allRfqData.forEach((item) => countrySet.add(item.country));

    return Array.from(countrySet);
  }, [allStockData, allOfferData, allRequirementData, allRfqData]);

  // State for each dropdown
  const [selectedStockCountry, setSelectedStockCountry] = useState("All");
  const [selectedOfferCountry, setSelectedOfferCountry] = useState("All");
  const [selectedRequirementCountry, setSelectedRequirementCountry] =
    useState("All");
  const [selectedRfqCountry, setSelectedRfqCountry] = useState("All");

  // Filtered data based on respective country selections
  const stockData =
    selectedStockCountry === "All"
      ? allStockData
      : allStockData.filter((item) => item.country === selectedStockCountry);

  const offerData =
    selectedOfferCountry === "All"
      ? allOfferData
      : allOfferData.filter((item) => item.country === selectedOfferCountry);

  const requirementData =
    selectedRequirementCountry === "All"
      ? allRequirementData
      : allRequirementData.filter(
          (item) => item.country === selectedRequirementCountry
        );

  const rfqData =
    selectedRfqCountry === "All"
      ? allRfqData
      : allRfqData.filter((item) => item.country === selectedRfqCountry);

  return (
    <div className="container-fluid mt-2">
      <div className="card">
        <div className="row">
          {/* Top Cards */}
          <div className="row g-4 mb-4">
            {loadingStates.cards
              ? // Show placeholder cards while loading
                Array.from({ length: 4 }, (_, index) => (
                  <div className="col-12 col-sm-6 col-md-3" key={index}>
                    <CardPlaceholder />
                  </div>
                ))
              : // Show actual cards when loaded
                cards.map((card, index) => (
                  <div className="col-12 col-sm-6 col-md-3" key={index}>
                    <div
                      className="d-flex align-items-center shadow-sm rounded p-3 h-100"
                      style={{ backgroundColor: card.bgColor }}
                    >
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle me-3"
                        style={{
                          backgroundColor: "#ffffff",
                          color: card.iconColor,
                          width: "40px",
                          height: "40px",
                          boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                        }}
                      >
                        {card.icon}
                      </div>
                      <div>
                        <div
                          className="text-muted"
                          style={{ fontWeight: "bold", fontSize: "16px" }}
                        >
                          {card.title}
                        </div>
                        <div
                          className="fw-bold"
                          style={{ color: card.textColor }}
                        >
                          {card.value}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
          </div>
        </div>
        <div className="row">
          {/* Pie Chart */}
          <div className="col-lg-6 col-md-12">
            <div
              className="bg-white p-4 rounded shadow-sm card"
              style={{ border: "1px solid #ddd" }}
            >
              <h6 className="fw-bold mb-4" style={{ fontSize: "16px" }}>
                <LocalOffer
                  style={{
                    verticalAlign: "middle",
                    marginRight: "8px",
                    color: "#1976d2",
                  }}
                />{" "}
                Offers by Status
              </h6>
              {loadingStates.pieChart ? (
                <PlaceholderLoader height="300px" />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, percent }) =>
                        `${name} (${(percent * 100).toFixed(0)}%)`
                      }
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend
                      layout="horizontal"
                      verticalAlign="bottom"
                      align="center"
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          {/* Stock Report Country Wise Bar Chart */}
          <div className="col-lg-6 col-md-12">
            <div
              className="bg-white p-4 rounded shadow-sm card"
              style={{ border: "1px solid #ddd" }}
            >
              <h6 className="fw-bold mb-2" style={{ fontSize: "16px" }}>
                <LocalOffer
                  style={{
                    verticalAlign: "middle",
                    marginRight: "8px",
                    color: "#1976d2",
                  }}
                />{" "}
                Stock Report Country Wise
              </h6>
              {loadingStates.stockChart ? (
                <PlaceholderLoader height="290px" />
              ) : (
                <>
                  <div style={{ marginBottom: "10px" }}>
                    <label>Filter by Country:</label>
                    <select
                      value={selectedStockCountry}
                      onChange={(e) => setSelectedStockCountry(e.target.value)}
                      className="form-control"
                      style={{ marginLeft: "10px" }}
                    >
                      {countries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={stockData}
                      margin={{ top: 10, right: 0, left: 0, bottom: 50 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        angle={-15}
                        textAnchor="end"
                        height={5}
                        style={{ fontSize: "9px" }}
                      />
                      <YAxis />
                      <Tooltip />
                      <Bar
                        dataKey="stock"
                        fill="#bccf75"
                        radius={[5, 5, 0, 0]}
                        name="Stock (MT)"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </>
              )}
            </div>
          </div>
        </div>

        {/* New Row with Offer and Requirement Reports */}
        <div className="row mt-4">
          <div className="col-lg-6 col-md-12">
            <div
              className="bg-white p-4 rounded shadow-sm card"
              style={{ border: "1px solid #ddd" }}
            >
              <h6 className="fw-bold mb-2" style={{ fontSize: "16px" }}>
                <LocalOffer
                  style={{
                    verticalAlign: "middle",
                    marginRight: "8px",
                    color: "#1976d2",
                  }}
                />
                Offer Report Country Wise
              </h6>
              {loadingStates.offerChart ? (
                <PlaceholderLoader height="290px" />
              ) : (
                <>
                  <div style={{ marginBottom: "10px" }}>
                    <label>Filter by Country:</label>
                    <select
                      value={selectedOfferCountry}
                      onChange={(e) => setSelectedOfferCountry(e.target.value)}
                      className="form-control"
                      style={{ marginLeft: "10px" }}
                    >
                      {countries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={offerData}
                      margin={{ top: 10, right: 0, left: 0, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="country" />
                      <YAxis />
                      <Tooltip />
                      <Bar
                        dataKey="offers"
                        fill="#d7590d"
                        radius={[5, 5, 0, 0]}
                      >
                        {offerData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill="#8C84E2" />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </>
              )}
            </div>
          </div>
          <div className="col-lg-6 col-md-12">
            <div
              className="bg-white p-4 rounded shadow-sm card"
              style={{ border: "1px solid #ddd" }}
            >
              <h6 className="fw-bold mb-2" style={{ fontSize: "16px" }}>
                <LocalOffer
                  style={{
                    verticalAlign: "middle",
                    marginRight: "8px",
                    color: "#1976d2",
                  }}
                />{" "}
                Requirement Report Country Wise
              </h6>
              {loadingStates.requirementChart ? (
                <PlaceholderLoader height="290px" />
              ) : (
                <>
                  <div style={{ marginBottom: "10px" }}>
                    <label>Filter by Country:</label>
                    <select
                      value={selectedRequirementCountry}
                      onChange={(e) =>
                        setSelectedRequirementCountry(e.target.value)
                      }
                      className="form-control"
                      style={{ marginLeft: "10px" }}
                    >
                      {countries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={requirementData}
                      margin={{ top: 10, right: 0, left: 0, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="country" />
                      <YAxis />
                      <Tooltip />
                      <Bar
                        dataKey="requirements"
                        fill="#ffbe00"
                        radius={[5, 5, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </>
              )}
            </div>
          </div>
        </div>

        {/* RFQ Report Country Wise - Added after Requirement Report */}
        <div className="row mt-4">
          <div className="col-12 col-lg-6">
            <div
              className="bg-white p-4 rounded shadow-sm card"
              style={{ border: "1px solid #ddd" }}
            >
              <h6 className="fw-bold mb-2" style={{ fontSize: "16px" }}>
                <LocalOffer
                  style={{
                    verticalAlign: "middle",
                    marginRight: "8px",
                    color: "#1976d2",
                  }}
                />{" "}
                RFQ Report Country Wise
              </h6>
              {loadingStates.rfqChart ? (
                <PlaceholderLoader height="290px" />
              ) : (
                <>
                  <div style={{ marginBottom: "10px" }}>
                    <label>Filter by Country:</label>
                    <select
                      value={selectedRfqCountry}
                      onChange={(e) => setSelectedRfqCountry(e.target.value)}
                      className="form-control"
                      style={{ marginLeft: "10px" }}
                    >
                      {countries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={rfqData}
                      margin={{ top: 10, right: 0, left: 0, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="country" />
                      <YAxis />
                      <Tooltip />
                      <Bar
                        dataKey="rfqs"
                        fill="#61822a"
                        radius={[5, 5, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Trade Volume by Month Bar Chart */}

        {/* Table Card */}
        <div className="row mt-4">
          <div className="col-12">
            <Paper elevation={2} sx={{ p: 3 }}>
              <h6 className="fw-bold mb-4" style={{ fontSize: "16px" }}>
                Recent Activity
              </h6>
              {loadingStates.recentActivity ? (
                <PlaceholderLoader height="400px" />
              ) : (
                <div className="table-content">
                  <DataTable
                    columns={columns}
                    data={tableData}
                    // onRowClick={handleRowClick}
                  />
                </div>
              )}
            </Paper>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyTradeDashboard;
